/**
 * User Context.
 *
 * Provides the authenticated user's account data and notifications.
 * This is the base context for ALL authenticated users regardless of role.
 *
 * Architecture:
 * - AuthProvider: Session management, login/logout
 * - UserProvider: Account profile, notifications (all authenticated users)
 * - Role contexts: Provider/Purchaser/Admin-specific data
 */

'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/lib/auth/AuthProvider';
import { queryKeys } from '@/lib/query/queryKeys';
import { purchaserApi } from '@/lib/api/purchaser';
import type {
  AccountProfile,
  Notification,
  ConversationSummary
} from '@/lib/api/purchaser/types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type UserContextValue = {
  /** Current user's account profile */
  account: AccountProfile | null;
  /** Whether account data is loading */
  isLoadingAccount: boolean;
  /** Account loading error */
  accountError: Error | null;
  /** Refetch account data */
  refetchAccount: () => Promise<void>;

  /** Notifications list */
  notifications: Notification[];
  /** Whether notifications are loading */
  isLoadingNotifications: boolean;
  /** Unread notifications count */
  unreadNotificationsCount: number;
  /** Mark notification as read */
  markNotificationRead: (id: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllNotificationsRead: () => Promise<void>;

  /** Conversations list */
  conversations: ConversationSummary[];
  /** Whether conversations are loading */
  isLoadingConversations: boolean;
  /** Unread conversations count */
  unreadConversationsCount: number;
  /** Refetch conversations */
  refetchConversations: () => Promise<void>;
};

const UserContext = createContext<UserContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Provider Component
// ─────────────────────────────────────────────────────────────────────────────

export function UserProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const isAuthenticated = status === 'authenticated';

  // Account profile query
  const accountQuery = useQuery({
    queryKey: queryKeys.account.profile(),
    queryFn: purchaserApi.getProfile,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000 // 30 minutes
  });

  // Notifications query
  const notificationsQuery = useQuery({
    queryKey: queryKeys.account.notifications(),
    queryFn: purchaserApi.getNotifications,
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000 // Poll every minute
  });

  // Conversations query
  const conversationsQuery = useQuery({
    queryKey: queryKeys.account.conversations.list(),
    queryFn: () => purchaserApi.getConversations(),
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000 // Poll every minute
  });

  const refetchAccount = async () => {
    await accountQuery.refetch();
  };

  const refetchConversations = async () => {
    await conversationsQuery.refetch();
  };

  const markNotificationRead = async (id: string) => {
    await purchaserApi.markNotificationRead(id);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.account.notifications()
    });
  };

  const markAllNotificationsRead = async () => {
    await purchaserApi.markAllNotificationsRead();
    await queryClient.invalidateQueries({
      queryKey: queryKeys.account.notifications()
    });
  };

  const notifications = notificationsQuery.data?.items ?? [];
  const unreadNotificationsCount = notifications.filter((n) => !n.read).length;

  const conversations = conversationsQuery.data?.items ?? [];
  const unreadConversationsCount = conversations.filter(
    (c) => c.unreadCount > 0
  ).length;

  const value: UserContextValue = {
    account: accountQuery.data ?? null,
    isLoadingAccount: accountQuery.isLoading,
    accountError: accountQuery.error as Error | null,
    refetchAccount,

    notifications,
    isLoadingNotifications: notificationsQuery.isLoading,
    unreadNotificationsCount,
    markNotificationRead,
    markAllNotificationsRead,

    conversations,
    isLoadingConversations: conversationsQuery.isLoading,
    unreadConversationsCount,
    refetchConversations
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useUser() {
  const value = useContext(UserContext);
  if (!value) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return value;
}

/**
 * Hook to optionally get the user context.
 * Returns null if not within a UserProvider (e.g., public pages).
 */
export function useOptionalUser() {
  return useContext(UserContext);
}

/**
 * Hook to get just the account data.
 */
export function useAccount() {
  const { account, isLoadingAccount, accountError, refetchAccount } = useUser();
  return {
    account,
    isLoading: isLoadingAccount,
    error: accountError,
    refetch: refetchAccount
  };
}

/**
 * Hook to get just notifications.
 */
export function useNotifications() {
  const {
    notifications,
    isLoadingNotifications,
    unreadNotificationsCount,
    markNotificationRead,
    markAllNotificationsRead
  } = useUser();
  return {
    notifications,
    isLoading: isLoadingNotifications,
    unreadCount: unreadNotificationsCount,
    markRead: markNotificationRead,
    markAllRead: markAllNotificationsRead
  };
}

/**
 * Hook to get just conversations.
 */
export function useConversations() {
  const {
    conversations,
    isLoadingConversations,
    unreadConversationsCount,
    refetchConversations
  } = useUser();
  return {
    conversations,
    isLoading: isLoadingConversations,
    unreadCount: unreadConversationsCount,
    refetch: refetchConversations
  };
}
